import * as React from 'react'
import moment from 'moment'

import Schedule from './Schedule'
import { TimesheetActionBar, TimesheetViewMode } from '../components'
import { SERVICE_MONTH_FORMAT, SERVICE_DATE_FORMAT } from '@/app/constants/date'
import useTimesheetService from '@/modules/drivers/services/TimesheetService'
import { useDrivers } from '@/modules/shared/context/drivers/DriversContext'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { toast } from '@/lib/toasts/api'
import { ConfirmModal } from '@/modules/drivers/modals/ConfirmModal'

import { formatDate } from '@/modules/shared/utils/date'
import { DriverBonusAndRewardsModal } from '@/modules/drivers/modals/timesheet/DriverBonusAndRewardsModal'
import { useGroupCalculateHandlers } from './Group.calculate.handlers'
import {
  MISSING_QUARTER_RATE_CODE,
  hasErrorCode,
  hasOutOfDateQuarterRateNotice,
  useOutOfDateRateConfirm,
} from './useOutOfDateRateConfirm'
import { fetchExpectedNettoFromEasyTms } from '@/lib/api/company/timesheet/groupedTimesheet/fetchExpectedNettoFromEasyTms'
import { getGroupedTimesheet } from '@/lib/api/company/timesheet/groupedTimesheet/getGroupedTimesheet'
import { pickReward } from './payroll/utils'
import { useSaveAndCalculate } from './payroll/hooks/useSaveAndCalculate'
import { useBonusRewardsToolbar } from './payroll/hooks/useBonusRewardsToolbar'
import { PayrollDriverRow } from '@/lib/api/company/timesheet/groupedTimesheet/types'
import { useTask } from '@/modules/shared/context/TaskContext'
import { TaskType } from '@/app/constants/taskType'
import { TaskStatus } from '@/app/constants/taskStatus'
import { PrintActionContainer } from '@/modules/printouts/containers/PrintActionContainer'
import { TimesheetPrintShortcuts } from '@/modules/printouts/components/TimesheetPrintShortcuts'
import { ReportType } from '@/app/constants/reportType'
import { resolveDate } from '@/modules/shared/utils/resolveDate'
import Timesheet from '../timesheet/Timesheet'
import TimesheetProvider from '../timesheet/context/useTimesheet'
import { TimesheetExplainDrawer } from '../timesheet/explain/TimesheetExplainDrawer'
import { TimesheetExplainModal } from '../timesheet/explain/TimesheetExplainModal'
import TimesheetInfoPopup from '../timesheet/components/popup/TimesheetInfoPopup'
import AmountsModal from '../components/AmountsModal'

import type { TimesheetModel } from '@/app/models/timesheetModel'
import { v4 as uuidv4 } from 'uuid'
import { useConfirm } from '@/modules/shared/context/ConfirmContext'
import { useBlocker, useNavigate, useLocation, useParams } from 'react-router-dom'
import { useTimesheetPrefsStore } from '@/modules/drivers/stores/timesheetStore'
import { useScheduleStore } from '@/modules/drivers/stores/scheduleStore'
import { useDateStore } from '@/modules/drivers/stores/dateStore'
import { useQuery, useQueryClient } from '@tanstack/react-query'

const availableReports = [
  ReportType.TIMESHEET_FOREIGN_AMOUNTS_REPORT,
  ReportType.TIMESHEET_LUMPSUM_SETTLEMENT,
  ReportType.TIMESHEET_LUMPSUM_UTILIZATION,
  ReportType.TIMESHEET_APPLICATION_FOR_LEAVE_FOR_DRIVERS,
  ReportType.TIMESHEET_REWARD_FOR_DRIVERS,
  ReportType.TIMESHEET,
  ReportType.TIMESHEET_LEAVES_CALCULATION,
  ReportType.TIMESHEET_LEAVE_EQUIVALENT_CALCULATION,
  ReportType.REWARDS_TO_ACCEPT_REPORT,
  ReportType.MOBILITY_PACKAGE_PAYROL_REPORT,
  ReportType.TRIP_COSTS,
  ReportType.TIMESHEET_AMOUNTS_REPORT,
  ReportType.TIMESHEET_CALCULATION_AUDIT,
  ReportType.TIMESHEET_PAYMENT_DETAILS,
  ReportType.TIMESHEET_DETAILED_WAGE_REPORT,
  ReportType.TIMESHEET_YEAR_SUMMARY,
  ReportType.TIMESHEET_FOREIGN_AMOUNT_SUMMARY,
  ReportType.TIMESHEET_LUMPSUM_SETTLEMENT_SUMMARY,
  ReportType.TIMESHEET_WORK_COSTS,
  ReportType.TIMESHEET_PAYMENT_REPORT,
  ReportType.DRIVER_WORKING_DAYS,
  ReportType.TIMESHEET_LEAVE_DAYS_REPORT,
  // WPF parity - not yet enabled:
  // ReportType.SELECTED_TIMESHEET_MOBILITY_PACKAGE_PAYROLL_REPORT,
  // ReportType.SELECTED_REWARDS_TO_ACCEPT_REPORT,
]

interface SingleProps {
  companyId: string
  driverId?: string
}

// Calculate default month (last month)
const getDefaultMonthKey = () => {
  const now = new Date()
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
  const month = lastMonth.getMonth() + 1
  const year = lastMonth.getFullYear()
  return `${year}-${String(month).padStart(2, '0')}`
}

const Single: React.FC<SingleProps> = ({
  companyId,
  driverId: driverIdProp,
}) => {
  const params = useParams<{ driverId?: string; id?: string; month?: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const [isCalculating, setIsCalculating] = React.useState(false)
  const [showChart, setShowChart] = React.useState(false)
  const driverId = driverIdProp || params.driverId || params.id
  const [isOpenAmountsModal, setIsOpenAmountsModal] = React.useState(false)
  const [amountsModalTab, setAmountsModalTab] = React.useState<'pln' | 'foreign'>('pln')

  // Bonus and reward modal
  const [isBonusAndRewardModalOpen, setBonusAndRewardModalOpen] =
    React.useState<boolean>(false)
  const toggleBonusAndRewardModal = () =>
    setBonusAndRewardModalOpen((open) => !open)

  const refreshSchedule = useScheduleStore((s) => s.refresh)

  const monthFromUrl = React.useMemo(() => {
    if (params.month) {
      const parsed = moment(params.month, SERVICE_MONTH_FORMAT)
      if (parsed.isValid()) return parsed.format(SERVICE_MONTH_FORMAT)
    }
    return null
  }, [params.month])

  // Global month lives in useDateStore — the single source of truth.
  const storeMonth = useDateStore((s) => s.month)
  const setStoreMonth = useDateStore((s) => s.setMonth)

  // True while a setStoreMonth call originated from a URL change (not a user
  // action). Store→URL checks this so it doesn't redundantly navigate back.
  // A ref (not state) keeps it synchronous — effects need to read/write it
  // within the same flush without causing re-renders.
  const isUrlDrivenChange = React.useRef(false)

  // URL → Store: when the URL month changes (deep-link, browser back/forward),
  // update the store so the rest of the UI reflects the correct month.
  React.useEffect(() => {
    if (monthFromUrl && monthFromUrl !== useDateStore.getState().month) {
      isUrlDrivenChange.current = true
      setStoreMonth(monthFromUrl)
    }
  }, [monthFromUrl, setStoreMonth])

  // Store → URL: when the store month changes (nav arrows, month picker),
  // rewrite the `/month/:month` segment in the URL.
  // Skips when the store change was itself triggered by a URL change (the flag
  // above), which covers both direct URL nav and React Strict Mode's
  // double-invocation of effects (refs survive the remount, state does not —
  // so getState() already reflects the URL→Store update on the second run).
  // Intentionally excludes params.month from deps — browser back/forward is
  // handled by the URL→Store effect above; including params.month here would
  // cause this effect to fire on browser navigation and replace the URL back.
  React.useEffect(() => {
    if (isUrlDrivenChange.current) {
      isUrlDrivenChange.current = false
      return
    }
    // Use getState() rather than the render-snapshot `storeMonth` so that
    // in Strict Mode's second effect run we see the value already written by
    // URL→Store's first run (Zustand is synchronous; React state is not).
    const currentStoreMonth = useDateStore.getState().month
    if (!currentStoreMonth || !params.month) return
    if (currentStoreMonth === params.month) return
    const newPath = location.pathname.replace(
      /\/month\/[^/]+/,
      `/month/${currentStoreMonth}`,
    )
    if (newPath !== location.pathname) {
      navigate(newPath, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeMonth, navigate])

  const selectedMonthKey =
    storeMonth || monthFromUrl || getDefaultMonthKey()

  const setSelectedMonthKey = setStoreMonth

  const month = React.useMemo(() => {
    const parsed = moment(selectedMonthKey, SERVICE_MONTH_FORMAT)
    return parsed.isValid() ? parsed : moment().startOf('month')
  }, [selectedMonthKey])

  const yearMonth = month.format(SERVICE_MONTH_FORMAT)

  const [selectedCalculateMonths, setSelectedCalculateMonths] = React.useState<
    string[]
  >(() => [yearMonth])

 
  const [seededMonth, setSeededMonth] = React.useState(yearMonth)
  if (seededMonth !== yearMonth) {
    setSeededMonth(yearMonth)
    setSelectedCalculateMonths([yearMonth])
  }

  const storedViewMode = useTimesheetPrefsStore((s) => s.viewMode)
  const setStoredViewMode = useTimesheetPrefsStore((s) => s.setViewMode)
  const viewMode: 'timesheet' | 'schedule' | 'single-view' =
    storedViewMode === 'bonus-reward' || storedViewMode === 'schedule'
      ? 'schedule'
      : 'timesheet'
  const setViewMode = React.useCallback(
    (next: 'timesheet' | 'schedule' | 'single-view') => {
      const mode: TimesheetViewMode =
        next === 'schedule' ? 'schedule' : 'single-view'
      setStoredViewMode(mode)
    },
    [setStoredViewMode],
  )

  const [scheduleYear, setScheduleYear] = React.useState<number>(() => {
    return new Date().getFullYear()
  })

  // State for remove confirmation modal
  const [showRemoveModal, setShowRemoveModal] = React.useState(false)
  const [timesheetKey, setTimesheetKey] = React.useState(0) // Key to force remount

  // State for calculation info (date and user)
  const [calculationInfo, setCalculationInfo] = React.useState<{
    date: string | null
    user: string | null
    userEmail: string | null
  }>({ date: null, user: null, userEmail: null })

  // Preview state: holds unsaved calculation result
  const [previewTimesheet, setPreviewTimesheet] =
    React.useState<TimesheetModel | null>(null)
  const [showOldTimesheet, setShowOldTimesheet] = React.useState(false)
  const [isSaving, setIsSaving] = React.useState(false)

  // Services and contexts
  const { removeTimesheet, previewCalculateTimesheet, getDriverLeaveSummary } =
    useTimesheetService()
  const { drivers, basicDrivers } = useDrivers()
  const { t } = useLocalize()
  const { completedTasks } = useTask()
  const queryClient = useQueryClient()


  const scheduleRefreshKey = useScheduleStore((s) => s.refreshKey)
  const leaveSummaryYear = month.year()
  const { data: leaveSummary } = useQuery({
    queryKey: [
      'driver-leave-summary',
      companyId,
      driverId,
      leaveSummaryYear,
      scheduleRefreshKey,
    ],
    queryFn: () =>
      getDriverLeaveSummary({
        companyId,
        driverId: driverId as string,
        year: leaveSummaryYear,
      }),
    enabled: !!companyId && !!driverId,
    retry: false,
  })

  // Zgoda na starszy kurs kwartalny wyrazona przy podgladzie obowiazuje tez przy "Zapisz",
  // zeby nie pytac drugi raz o to samo.
  const previewRateAcceptedRef = React.useRef(false)

  // Wspolny "koniec przeliczenia": woła go i sciezka przez TaskContext (fallback
  // _handleCalculate), i useSaveAndCalculate, ktory wlasnych zadan do TaskContext nie dodaje.
  const finishCalculation = React.useCallback(() => {
    setIsCalculating(false)
    previewRateAcceptedRef.current = false
    setIsSaving(false)
    setPreviewTimesheet(null)
    setShowOldTimesheet(false)
    refreshSchedule()
    setTimesheetKey((prev) => prev + 1)
    // The amounts preview reads persisted TimesheetAmounts, so it only becomes correct
    // once the FileProcessor job has written them. Invalidating on task completion (and not
    // when "Przelicz" is clicked) is what keeps the modal from re-caching pre-calculation values.
    queryClient.invalidateQueries({ queryKey: ['amounts-preview'] })
    queryClient.invalidateQueries({ queryKey: ['foreign-amounts-preview'] })
    // Przeliczenie kasuje flagi nieaktualnosci - znacznik zadan przy kierowcu ma zniknac
    // razem z zakonczeniem zadania.
    queryClient.invalidateQueries({
      predicate: (query) => query.queryKey[0] === 'driverRequiredActions',
    })
  }, [queryClient, refreshSchedule])

  // Refresh timesheet when a Timesheet task completes or errors
  React.useEffect(() => {
    const done = completedTasks.some(
      (task) =>
        task.type === TaskType.Timesheet &&
        (task.status === TaskStatus.Completed ||
          task.status === TaskStatus.Error),
    )
    if (done) {
      finishCalculation()
    }
  }, [completedTasks, finishCalculation])

  // Get driver name for confirmation message
  const driverName = React.useMemo(() => {
    if (!driverId) return ''
    const basic = basicDrivers.find((d) => String(d.id) === String(driverId))
    if (basic?.fullName) return basic.fullName
    const driver = drivers.find((d) => String(d.id) === String(driverId))
    return (
      driver?.fullName ||
      `${driver?.surname || ''} ${driver?.name || ''}`.trim() ||
      ''
    )
  }, [driverId, drivers, basicDrivers])


  const calculationPeriod = React.useMemo(() => {
    const year = month.year()
    const displayedMonth = month.month() + 1

    const months = Array.from(
      new Set(
        selectedCalculateMonths
          .filter((key) => parseInt(key.split('-')[0], 10) === year)
          .map((key) => parseInt(key.split('-')[1], 10))
          .filter((m) => m >= 1 && m <= 12),
      ),
    ).sort((a, b) => a - b)

    if (months.length === 0) return { year, months: [displayedMonth] }
    return { year, months }
  }, [selectedCalculateMonths, month])

  // Ten sam, trwaly stan paska co ekran grupowy (klucz 'bonus-reward-toolbar'), zeby
  // "Przelicz" z obu ekranow wysylalo te same flagi silnika.
  const { state: toolbarState } = useBonusRewardsToolbar()
  const domesticBusinessTripDateType =
    toolbarState.domesticBusinessTripDateType ?? undefined

  const {
    requestConfirm: confirmOutOfDateExchangeRate,
    modal: outOfDateRateConfirmModal,
  } = useOutOfDateRateConfirm()

  const confirmRateForCalculate = React.useCallback(
    () =>
      previewRateAcceptedRef.current
        ? Promise.resolve(true)
        : confirmOutOfDateExchangeRate(),
    [confirmOutOfDateExchangeRate],
  )

  const { handleCalculate: _handleCalculate } = useGroupCalculateHandlers({
    period: calculationPeriod,
    companyId,
    selectedMonthKey: yearMonth,
    driverIds: [driverId],
    confirmOutOfDateExchangeRate: confirmRateForCalculate,
  })

  const handleMonthsChange = React.useCallback((months: string[]) => {
    setSelectedCalculateMonths(months)
  }, [])

  /**
   * Wiersz kierowcy + konfiguracja premii, budowane z TEGO SAMEGO zrodla co ekran grupowy
   * (groupedTimesheet + pickReward). Ma to znaczenie dla NAGRODY: groupedTimesheet nadpisuje
   * RewardRate zapisanym na miesiacu skladnikiem NAGRODA
   * (GroupedTimesheetRequestHandler.LoadBonusConfiguration), a getBonusConfiguration zwraca goly
   * RewardRate z konfiguracji.
   *
   * Wysylamy tylko te pola, ktore CalculateTimesheetJob nadpisuje BEZ sprawdzania, czy przyszly
   * (nagroda, premia, potracenie komornicze, dodatki urlopowe, useValuesFromContract) -
   * pominiecie ktoregos wyzerowaloby je na ewidencji. Pola chronione warunkiem HasValue (urlop,
   * chorobowe, benefity, delegacje krajowe, godziny ekwiwalentu) swiadomie zostaja niewyslane,
   * zeby job zachowal to, co ma zapisane.
   */
  const buildCalculationInput = React.useCallback(async (): Promise<{
    row: PayrollDriverRow
    config: any
  } | null> => {
    // useSaveAndCalculate liczy dokladnie jeden miesiac (props.month), wiec przy kilku
    // zaznaczonych miesiacach zostajemy przy dotychczasowej, jednoprzebiegowej sciezce.
    if (calculationPeriod.months.length !== 1) return null

    const numericDriverId = parseInt(driverId, 10)
    if (!companyId || !numericDriverId) return null

    const year = calculationPeriod.year
    const month = calculationPeriod.months[0]

    // Oczekiwane netto z EasyTMS/BFM - tak samo jak robi to ekran grupowy
    // (GroupPayrollView.handleCalculate). Best-effort: brak akronimu, brak danych w BFM albo
    // blad integracji => liczymy z netto, ktore jest juz na ewidencji.
    let easyTmsNetto: number | null = null
    try {
      const { data } = await fetchExpectedNettoFromEasyTms({
        companyId,
        year,
        month,
        driverIds: [numericDriverId],
      })
      const nettoRow = (data || []).find((r) => r.driverId === numericDriverId)
      if (nettoRow?.found && nettoRow.expectedNettoAmount != null) {
        easyTmsNetto = nettoRow.expectedNettoAmount
      }
    } catch (err) {
      console.error('EasyTMS expected netto fetch error:', err)
      toast.error({ text: t('timesheet.easytms-fetch-error') })
    }

    let row: PayrollDriverRow | undefined
    try {
      const { data: rows } = await getGroupedTimesheet({
        companyId,
        driversIds: [numericDriverId],
        year,
        month,
        showBonusConfiguration: true,
        domesticBusinessTripDateType,
      })
      row = (rows || []).find((r) => r.driver.id === numericDriverId)
    } catch (err) {
      // Bez odczytanej konfiguracji nie da sie wyslac zadnego pola, nie niszczac reszty kwot.
      console.error('Bonus configuration fetch error:', err)
      return null
    }
    if (!row) return null

    if (easyTmsNetto != null) {
      toast.success({
        text: t('timesheet.easytms-fetch-success-driver').replace(
          '{0}',
          String(easyTmsNetto),
        ),
      })
    }

    // Netto wysylamy TYLKO gdy je mamy. Pole jest nullowalne wlasnie po to: wyslane 0 job
    // potraktowalby jak cel rowny zeru i wyzerowal wyplate, a pominiete oddaje sterowanie
    // temu, co jest zapisane na ewidencji (IsNettoAmountModifiedByUser / wyliczenie z umowy).
    const expectedNettoAmount =
      easyTmsNetto ??
      (row.expectedNettoAmount != null && row.expectedNettoAmount > 0
        ? Number(row.expectedNettoAmount)
        : undefined)

    return {
      row,
      config: {
        driverId: numericDriverId,
        ...(expectedNettoAmount !== undefined && { expectedNettoAmount }),
        // Ta sama funkcja, ktorej uzywa ekran grupowy - dzieki temu "Przelicz" z obu ekranow
        // startuje z ta sama nagroda.
        rewardRate: pickReward(row),
        bonusRate: row.bonus != null ? Number(row.bonus) : 0,
        // Na sztywno false, dokladnie jak useSaveAndCalculate.buildBonusConfigs na ekranie
        // grupowym, i nie jest to kosmetyka: przy true UpdateConfiguration czysci
        // UserModifiedRates i NIE zapisuje RewardRateModification, wiec PrepareTimesheetData
        // odtwarza skladnik NAGRODA z umowy (czyli 0) i przeliczenie kasuje nagrode razem
        // z brutto. Przy false nagroda wraca do UserModifiedRates i przezywa regeneracje kwot.
        useValuesFromContract: false,
        bailiffDeductionAmount: row.bailiffDeductionAmount ?? null,
        timesheetAdditionForLeaves: row.timesheetAdditionForLeaves ?? [],
      },
    }
  }, [companyId, driverId, calculationPeriod, domesticBusinessTripDateType, t])

  /**
   * "Przelicz" idzie DOKLADNIE tym samym przeplywem co ekran grupowy - tym samym hookiem,
   * tylko z jednym kierowcem na liscie. Dzieki temu przebieg 1 (liczenie z biezaca nagroda)
   * i przebieg 2 (nalozenie propozycji domykajacej wyplate do oczekiwanego netto) nie moga
   * sie rozjechac miedzy ekranami przy przyszlych zmianach. Przebiegu 3 nie ma nigdzie -
   * patrz REVERT_UNCLOSED_REWARDS_ENABLED.
   */
  const saveAndCalculate = useSaveAndCalculate({
    confirmOutOfDateExchangeRate: confirmRateForCalculate,
    companyId,
    month: calculationPeriod.months[0],
    year: calculationPeriod.year,
    domesticBusinessTripDateType,
    onSuccess: () => finishCalculation(),
    onError: () => finishCalculation(),
  })

  const runCalculate = React.useCallback(async (): Promise<boolean> => {
    const input = await buildCalculationInput()

    // Kilka miesiacy naraz albo nieodczytana konfiguracja: zostaje dotychczasowa sciezka
    // (jeden przebieg, bez domykania nagrody).
    if (!input) return _handleCalculate()

    saveAndCalculate.mutate({
      drivers: [{ id: input.row.driver.id, bonusConfiguration: input.config }],
      automaticOvertimeCompensation: toolbarState.automaticOvertimeCompensation,
      zusPaidFromAverageGross: toolbarState.zusPaidFromAverageGross,
    })
    return true
  }, [
    buildCalculationInput,
    _handleCalculate,
    saveAndCalculate,
    toolbarState.automaticOvertimeCompensation,
    toolbarState.zusPaidFromAverageGross,
  ])

  React.useEffect(() => {
    setPreviewTimesheet(null);
    setShowOldTimesheet(false);
    previewRateAcceptedRef.current = false
  }, [month])

  const handlePreviewCalculate = React.useCallback(async () => {
    // No prior saved calculation — save directly, no preview needed
    if (!calculationInfo.date) {
      setIsCalculating(true)
      const taskStarted = await runCalculate()
      if (!taskStarted) setIsCalculating(false)
      return
    }
    // Recalculating existing timesheet — show preview first
    previewRateAcceptedRef.current = false
    setIsCalculating(true)
    const runPreview = (outOfDateQuarterExchangeRateAccepted: boolean) =>
      previewCalculateTimesheet({
        companyId,
        drivers: [parseInt(driverId, 10)],
        months: [month.month() + 1],
        year: month.year(),
        outOfDateQuarterExchangeRateAccepted,
        bonusConfigurations: [],
        id: uuidv4(),
      })
    try {
      let result
      try {
        result = await runPreview(false)
      } catch (err) {
        // Brak kursu kwartalnego na liczony kwartal: ta sama zgoda co przy "Przelicz".
        if (!hasOutOfDateQuarterRateNotice(err)) throw err
        if (!(await confirmOutOfDateExchangeRate())) return
        previewRateAcceptedRef.current = true
        result = await runPreview(true)
      }
      setPreviewTimesheet(result)
    } catch (err) {
      console.error('Preview calculate error:', err)
      if (hasErrorCode(err, MISSING_QUARTER_RATE_CODE)) {
        toast.error({ text: t(`errorCode.${MISSING_QUARTER_RATE_CODE}`) })
      }
    } finally {
      setIsCalculating(false)
    }
  }, [companyId, driverId, month, previewCalculateTimesheet, calculationInfo.date, runCalculate, confirmOutOfDateExchangeRate, t])

  const handleSave = React.useCallback(async () => {
    // Recalculate in place — do NOT erase the timesheet first. removeTimesheet replaces the
    // part with an empty one, wiping ExpectedNettoAmount and resetting IsNettoAmountModifiedByUser,
    // which would discard a manually-entered "oczekiwane netto" (e.g. set on the group view).
    // This mirrors the WPF "Przelicz" command (CalculateTimesheet), which recalculates in place
    // and preserves the user value via IsNettoAmountModifiedByUser. Only the explicit
    // "Usuń ewidencję" action (handleConfirmRemove) erases — the WPF "Wyczyść" equivalent.
    setIsSaving(true)
    setIsCalculating(true)
    const taskStarted = await runCalculate()
    if (!taskStarted) {
      setIsCalculating(false)
      setIsSaving(false)
    }
  }, [runCalculate])

  const handleDiscard = React.useCallback(() => {
    previewRateAcceptedRef.current = false
    setPreviewTimesheet(null);
    setShowOldTimesheet(false)
  }, [])

  const confirm = useConfirm()
  const previewBlocker = useBlocker(!!previewTimesheet)
  React.useEffect(() => {
    if (previewBlocker.state !== 'blocked') return
    confirm({
      message: t('timesheet.discard-preview-confirm'),
      confirmText: t('configuration.unsaved-changes-leave'),
      cancelText: t('configuration.unsaved-changes-stay'),
    }).then((ok) => {
      if (ok) {
        setPreviewTimesheet(null)
        setShowOldTimesheet(false)
        previewBlocker.proceed()
      } else {
        previewBlocker.reset()
      }
    })
  }, [previewBlocker.state])

  const [showPrintModal, setShowPrintModal] = React.useState<boolean>(false)
  const [showExplainDrawer, setShowExplainDrawer] = React.useState<boolean>(false)
  const [showSettingsChangedPopup, setShowSettingsChangedPopup] =
    React.useState<boolean>(false)
  const [explainModalRowKey, setExplainModalRowKey] = React.useState<
    string | null
  >(null)
  const [loadedTimesheet, setLoadedTimesheet] =
    React.useState<TimesheetModel | null>(null)

  const handlePrint = React.useCallback(() => {
    setShowPrintModal(true)
  }, [])

  const handleExplain = React.useCallback(() => {
    setShowExplainDrawer(true)
  }, [])

  const handleExplainRow = React.useCallback((rowKey: string) => {
    setExplainModalRowKey(rowKey)
  }, [])

  const handleCloseDrawer = React.useCallback(() => {
    setShowExplainDrawer(false)
  }, [])

  const handleCloseModal = React.useCallback(() => {
    setExplainModalRowKey(null)
  }, [])

  const explainTimesheet =
    (previewTimesheet && !showOldTimesheet
      ? previewTimesheet
      : loadedTimesheet) ?? null

  const handleRefresh = React.useCallback(() => {
    refreshSchedule()
  }, [refreshSchedule])

  const handleViewToggle = React.useCallback((view: TimesheetViewMode) => {
    if (view === 'schedule') {
      setViewMode('schedule')
    } else if (view === 'single-view') {
      setViewMode('timesheet')
    }
  }, [])

  const handleRemove = React.useCallback(() => {
    setShowRemoveModal(true)
  }, [])

  const handleConfirmRemove = React.useCallback(async () => {
    if (!driverId || !companyId) {
      return
    }

    try {
      const firstDayOfMonth = month
        .clone()
        .startOf('month')
        .format(SERVICE_DATE_FORMAT)

      await removeTimesheet({
        companyId,
        driverId,
        date: firstDayOfMonth,
      })

      toast.success({
        text: t('timesheet.remove-timesheet-success'),
        content: t('timesheet.remove-timesheet-success-details'),
      })

      setShowRemoveModal(false)

      setTimesheetKey((prev) => prev + 1)

      handleRefresh()
    } catch (error) {
      console.error('Error removing timesheet:', error)
      toast.error({
        text: t('timesheet.remove-timesheet-error'),
        content: t('timesheet.remove-timesheet-error-details'),
      })
      setShowRemoveModal(false)
    }
  }, [driverId, companyId, month, removeTimesheet, t, handleRefresh])

  const resolvedDates = storeMonth
    ? resolveDate({ date: storeMonth }, { capDate: true })
    : null
  const range = resolvedDates?.state?.range ?? null

  if (!driverId) {
    return <div>No driver selected</div>
  }
  return (
    <div className="tw-flex tw-h-full tw-w-full tw-flex-col">
      {outOfDateRateConfirmModal}
      {isBonusAndRewardModalOpen && (
        <DriverBonusAndRewardsModal
          companyId={companyId}
          driverId={driverId}
          month={month.month() + 1}
          year={month.year()}
          handleClose={toggleBonusAndRewardModal}
        />
      )}
      {isOpenAmountsModal && (
        <AmountsModal
          onClose={() => setIsOpenAmountsModal(false)}
          companyId={companyId}
          driverId={driverId}
          yearMonth={yearMonth}
          initialTab={amountsModalTab}
        />
      )}
      <TimesheetActionBar
        variant="single"
        showChart={showChart} setShowChart={setShowChart}
        openAmounts={() => {
          setAmountsModalTab('pln')
          setIsOpenAmountsModal(true)
        }}
        openForeignAmounts={() => {
          setAmountsModalTab('foreign')
          setIsOpenAmountsModal(true)
        }}
        onCalculate={handlePreviewCalculate}
        onPrint={handlePrint}
        onExplain={handleExplain}
        onRefresh={handleRefresh}
        printShortcuts={
          <TimesheetPrintShortcuts
            companyId={companyId}
            driverIds={[driverId]}
            dateFrom={range?.dateFrom}
            dateTo={range?.dateTo}
          />
        }
        onBonusAndReward={toggleBonusAndRewardModal}
        onRemove={handleRemove}
        onViewToggle={handleViewToggle}
        currentViewMode={viewMode === 'schedule' ? 'schedule' : 'single-view'}
        hasCalculation={true}
    
        leaveSummary={
          calculationInfo.date || previewTimesheet ? (leaveSummary ?? null) : null
        }
        hasPreview={!!previewTimesheet}
        showOldTimesheet={showOldTimesheet}
        onSave={handleSave}
        onDiscard={handleDiscard}
        onToggleOldNew={() => setShowOldTimesheet((v) => !v)}
      />
      <div className="tw-flex tw-flex-1 tw-overflow-auto">
        <>
          {viewMode === 'timesheet' ? (
            <TimesheetProvider>
              <Timesheet
                isCalculating={isCalculating}
                setIsCalculating={setIsCalculating}
                key={timesheetKey}
                companyId={companyId}
                driverId={driverId}
                showChart={showChart}
                month={month}
                onCalculate={handlePreviewCalculate}
                selectedCalculateMonths={selectedCalculateMonths}
                onMonthsChange={handleMonthsChange}
                onTimesheetDataChange={(data) =>
                  setCalculationInfo({
                    date: data.calculationDate,
                    user: data.calculationUser,
                    userEmail: data.calculationUserEmail,
                  })
                }
                onTimesheetLoaded={setLoadedTimesheet}
                onExplainRow={handleExplainRow}
                previewTimesheet={previewTimesheet}
                showOldTimesheet={showOldTimesheet}
              />
            </TimesheetProvider>
          ) : (
            <Schedule
              isCalculating={isCalculating}
              companyId={companyId}
              driverId={driverId}
              year={scheduleYear}
            />
          )}
        </>
      </div>

      <PrintActionContainer
        companyId={companyId}
        dateFrom={range?.dateFrom}
        dateTo={range?.dateTo}
        driverIds={[driverId]}
        availableReports={availableReports}
        isOpen={showPrintModal}
        setOpen={setShowPrintModal}
      />

      <TimesheetExplainDrawer
        isOpen={showExplainDrawer}
        onClose={handleCloseDrawer}
        timesheet={explainTimesheet}
        onExplainRow={handleExplainRow}
      />

      <TimesheetExplainModal
        isOpen={!!explainModalRowKey}
        onClose={handleCloseModal}
        companyId={companyId}
        rowKey={explainModalRowKey}
        timesheet={explainTimesheet}
        onSettingsChanged={() => setShowSettingsChangedPopup(true)}
      />

      {showSettingsChangedPopup && (
        <TimesheetInfoPopup
          onCalculate={() => {
            handlePreviewCalculate()
          }}
          onClose={() => setShowSettingsChangedPopup(false)}
        />
      )}

 
      <ConfirmModal
        isOpen={showRemoveModal}
        onClose={() => setShowRemoveModal(false)}
        title={t('timesheet.remove-timesheet-title')}
        text={t('timesheet.remove-timesheet-message-single-month')
          .replace('{0}', driverName)
          .replace('{1}', formatDate(month.toDate(), 'MONTH_LONG_PL'))}
        onConfirm={handleConfirmRemove}
        actionText={t('common.delete')}
        content={[]}
      />
    </div>
  )
}

export default Single
